// Mig 20261016222316 — the tenant's plantings (farm_projects) for the spray
// planting pickers, plus the ONE selection state machine both capture modals
// share (SprayRecords + SprayTasks). The reconcile rules in usePlantingPicker
// are safety-critical and must never fork:
//   • NEVER silently change a selection the user (or a stored record/task)
//     already holds — an explicit "Whole block" sticks, and a stored planting
//     that has since ended/been cancelled stays on the form so the SERVER
//     can judge it (block mismatch degrades loudly with planting_degraded;
//     a mere window/status change saves fine and keeps true attribution).
//     Client-side wiping would persist a silent scope change, because the
//     payloads always carry the project_id key.
//   • Auto-select the sole live planting only while the field is PRISTINE —
//     no user touch, no edit/task seed.
// Query: status-filtered server-side (FINISHED statuses are unpickable on
// every date), paged past the PostgREST 1000-row cap, offlineFirst with a
// 24 h cache and a prewarm entry in offlineLookups (spray capture must work
// cold-offline). Orchard tenants have no farm_projects rows → pickers hidden.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useTenantPagedQuery } from '@/lib/tenantPagedQuery'
import { roundQty } from '@/lib/sprayMix'
import { activePlantingsOn, plantingAttributableOn, soleLivePlantingIdOn, PLANTING_FINISHED_STATUSES, type PlantingWindowFields } from '@/lib/plantings'
import { readState } from '@/lib/readState'

export interface PlantingOpt extends PlantingWindowFields {
  project_id:   string
  block_id:     string | null
  project_code: string
  /** The section's own name on the block ("Section A"), when one was given. */
  sub_section?: string | null
  size_ha:      number | null
  // The crop's commodity id — identity-copied onto farm_packhouse_loads
  // .commodity_id by the wagon flow (same global commodities catalogue).
  crop_id:      string | null
  crop:         { name: string } | null
}

/** The label embed every list/report row uses — ONE fragment so the four
 * query sites and the row types can't drift (pair with PlantingLabel on
 * the row). */
export const PROJECT_LABEL_EMBED = 'project:project_id ( project_code, crop:commodities(name) )'

/** The label embed plus the planting's own area, for a report that has to work
 *  out a per-hectare figure from a total quantity. The denominator is the
 *  PLANTING's hectares where one is attached and the block's otherwise — that
 *  rule is `plantingDosingHa`, not a local `?? block.hectares`. */
export const PROJECT_DOSING_EMBED =
  'project:project_id ( project_code, crop:commodities(name), size_ha )'

/** Row shape PROJECT_LABEL_EMBED returns (a name TS's Pick utility can't
 * collide with — HarvestPicksReport declares its own `Pick`). */
export type PlantingLabel = Pick<PlantingOpt, 'project_code' | 'crop'>

export const SPRAY_PLANTINGS_SELECT =
  'project_id, block_id, project_code, sub_section, size_ha, crop_id, status, cycle_start_date, expected_end_date, actual_end_date, crop:commodities(name)'

export function useTenantPlantings() {
  return useTenantPagedQuery<PlantingOpt>('spray-plantings', (lo, hi) =>
    supabase
      .from('farm_projects')
      .select(SPRAY_PLANTINGS_SELECT)
      .not('status', 'in', `(${PLANTING_FINISHED_STATUSES.join(',')})`)
      .is('deleted_at', null)
      .order('cycle_start_date', { ascending: false })
      .range(lo, hi) as unknown as PromiseLike<{ data: PlantingOpt[] | null; error: { message: string } | null }>,
  {
    // Offline-capture lookup profile (as useWorkerNames / the block lists):
    // serve the cache first in the field, refetch in the background, keep a
    // day. Plantings change a few times a week, not per keystroke.
    networkMode: 'offlineFirst',
    gcTime: 24 * 60 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Selection state for one capture form. `initialProjectId` is the edit/task
 * seed: pass the stored value (null = stored whole-block — sticky); leave
 * undefined for a pristine new form (sole live planting auto-selects).
 */
/** Window lookup for the PHI coexistence test (sprayHoldApplies). */
export function plantingWindowsById(
  plantings: PlantingOpt[] | undefined,
): ReadonlyMap<string, PlantingWindowFields> | null {
  if (!plantings) return null
  return new Map(plantings.map(p => [p.project_id, p]))
}

/**
 * What a PICKERLESS capture surface will attribute a find to.
 *
 * Quick log has no planting picker by design — it is the rapid,
 * GPS-picks-the-block path — so the attribution is resolved at save time from
 * the block's sole live planting. Returned as the id the save will use AND
 * the row it came from, because the modal has to be able to SHOW it: "sole
 * live planting" is a client-only test the database cannot make
 * (planting_attachable has no sole component), so a cache that has not yet
 * seen a SECOND planting will attach the first one and the trigger will wave
 * it through. Displaying what will be attached is what makes that visible.
 *
 * `on` is passed in rather than read from the clock here, so the caller can
 * feed the SAME date to this and to the row's observed_at — two reads either
 * side of midnight would attribute a find to a planting that was not live on
 * the date the row claims.
 */
export function useQuickLogPlanting(blockId: string, on: string) {
  const plantingsQ = useTenantPlantings()
  const rows = plantingsQ.data
  const projectId = soleLivePlantingIdOn(rows, blockId, on)
  const planting = projectId
    ? (rows ?? []).find(p => p.project_id === projectId) ?? null
    : null
  // F057: a null planting off a read that never answered is not "whole
  // block" — the caller must say the plantings are unread, not claim the
  // attribution. Same discriminator as PlantingSelect's `plantingsState`.
  return { projectId, planting, plantingsState: readState(plantingsQ) }
}

export function usePlantingPicker(opts: {
  blockId: string
  ymd: string
  initialProjectId?: string | null
  /** Harvest attribution: keep a planting pickable this many days past its
   *  close (cleanup/backfilled picks — the final pick usually CAUSES the
   *  close). Sprays leave it 0: you don't spray a removed crop. */
  graceDays?: number
}) {
  const plantingsQ = useTenantPlantings()
  const seeded = opts.initialProjectId !== undefined
  const [projectId, setProjectIdState] = useState(opts.initialProjectId ?? '')
  // Sticky once seeded or touched — see the module header's reconcile rules.
  const touchedRef = useRef(seeded)
  const setProjectId = useCallback((v: string) => {
    touchedRef.current = true
    setProjectIdState(v)
  }, [])

  // A BLOCK change always clears the selection and returns the field to
  // pristine (spec Phase B contract: "a block change must never silently
  // carry the old planting") — the auto-select effect below then resolves
  // afresh for the new block. Date changes deliberately do NOT clear: a
  // touched/seeded value stays pinned and honest (see PlantingSelect).
  const blockRef = useRef(opts.blockId)
  useEffect(() => {
    if (blockRef.current === opts.blockId) return
    blockRef.current = opts.blockId
    touchedRef.current = false
    setProjectIdState('')
  }, [opts.blockId])

  const graceDays = opts.graceDays ?? 0
  const blockPlantings = useMemo(
    () => graceDays > 0
      ? (plantingsQ.data ?? [])
          .filter(p => p.block_id === opts.blockId && plantingAttributableOn(p, opts.ymd, graceDays))
          .sort((a, b) => (a.cycle_start_date < b.cycle_start_date ? 1 : a.cycle_start_date > b.cycle_start_date ? -1 : 0))
      : activePlantingsOn((plantingsQ.data ?? []).filter(p => p.block_id === opts.blockId), opts.ymd),
    [plantingsQ.data, opts.blockId, opts.ymd, graceDays])

  // Pristine-only auto-select; also clears a pristine auto-pick when the
  // block/date moves. Never runs once seeded/touched, and never against an
  // unloaded list.
  useEffect(() => {
    if (touchedRef.current || plantingsQ.data === undefined) return
    setProjectIdState(blockPlantings.length === 1 ? blockPlantings[0].project_id : '')
  }, [blockPlantings, plantingsQ.data])

  // Resolve against the FULL list, not just the live-on-date subset: a
  // stored planting whose window/status moved still doses + labels off its
  // real row rather than silently falling back to whole-block figures.
  const selectedPlanting = useMemo(
    () => (projectId ? (plantingsQ.data ?? []).find(p => p.project_id === projectId) ?? null : null),
    [plantingsQ.data, projectId])

  // TRUE once the pristine auto-select can no longer flip the value — pass it
  // to an advisor chip as `verdictReady`, or the flip re-keys the telemetry
  // entity and logs a phantom exposure for advice the user never saw.
  // Pass it, do NOT mount on it: the first conjunct is false whenever the
  // plantings read FAILS, so a mount gate suppresses the chip's own
  // "check unavailable" arm on a food-safety signal.
  const settled = plantingsQ.data !== undefined
    && (touchedRef.current
      || blockPlantings.length !== 1
      || projectId === blockPlantings[0].project_id)

  return { plantingsQ, blockPlantings, projectId, setProjectId, selectedPlanting, settled }
}

/** Picker/chip label: `project_code · crop[ · N ha]` (parts drop when unknown).
 * size_ha is optional so a bare PROJECT_LABEL_EMBED row (PlantingLabel)
 * passes without a shim — absent short-circuits the ha part like null. */
export function plantingOptionLabel(
  p: Pick<PlantingOpt, 'project_code' | 'crop'> & { size_ha?: PlantingOpt['size_ha'] },
  opts?: { withHa?: boolean },
): string {
  return [
    p.project_code,
    p.crop?.name ?? null,
    (opts?.withHa ?? true) && p.size_ha != null ? `${roundQty(Number(p.size_ha))} ha` : null,
  ].filter(Boolean).join(' · ')
}

/** The chip/report variant — `project_code · crop`, no hectares. THE name
 * for the format every register chip and report column shares; feed it the
 * PROJECT_LABEL_EMBED row directly. */
export function plantingChipLabel(p: PlantingLabel): string {
  return plantingOptionLabel(p, { withHa: false })
}
